import {AssigneeRepositoryPicker} from '@github-ui/item-picker/AssigneePicker'

import {AlertIcon, TableIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {Suspense} from 'react'
import {MetadataFooterLoading} from './../metadata/MetadataFooterLoading'
import {useIssueCreateDataContext} from './../contexts/IssueCreateDataContext'
import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {MilestonePickerBase} from '@github-ui/item-picker/MilestonePicker'
import {LabelPickerBase} from '@github-ui/item-picker/LabelPicker'
import {ProjectPicker} from '@github-ui/item-picker/ProjectPicker'
import {SharedPicker} from '@github-ui/item-picker/SharedPicker'
import type {SharedMetadataProps} from './MetadataSelectors'

export const MetadataFooter = ({
  labelsRef,
  milestonesRef,
  projectsRef,
  setMilestoneCallback,
  canAssign,
  canLabel,
  canMilestone,
  canProject,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {repository, labels, setLabels, assignees, setAssignees, projects, setProjects, milestone} =
    useIssueCreateDataContext()

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    shortcutEnabled: optionConfig.singleKeyShortcutsEnabled,
    shortcutHintVisible: false,
  }

  return (
    <Suspense fallback={<MetadataFooterLoading />}>
      <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap'}}>
        {repository && (canAssign || assignees.length > 0) && (
          <AssigneeRepositoryPicker
            repoName={repository.name}
            repoOwner={repository.owner.login}
            readonly={!canAssign}
            assignees={assignees}
            participants={[]}
            onSelectionChange={setAssignees}
            {...sharedConfigProps}
          />
        )}
        {labelsRef && (canLabel || labels.length > 0) && (
          <LabelPickerBase
            readonly={!canLabel}
            labels={labels}
            labelQueryRef={labelsRef}
            onSelectionChange={setLabels}
            {...sharedConfigProps}
          />
        )}
        {projectsRef && repository && !optionConfig.scopedProjectTitle && (canProject || projects.length > 0) && (
          <ErrorBoundary
            fallback={<SharedPicker anchorText={'Projects are unavailable'} leadingIcon={AlertIcon} readonly />}
          >
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              projectQueryRef={projectsRef}
              readonly={!canProject}
              onSave={setProjects}
              selectedProjects={projects}
              firstSelectedProjectTitle={projects.length > 0 ? projects[0]!.title : ''}
              owner={repository.owner.login}
              repo={repository.name}
              includeClassicProjects={true}
              {...sharedConfigProps}
            />
          </ErrorBoundary>
        )}
        {/* Rendered when created from a Memex project */}
        {projectsRef && optionConfig.scopedProjectTitle && (
          <SharedPicker anchorText={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
        )}
        {milestonesRef && (canMilestone || milestone !== null) && (
          <MilestonePickerBase
            readonly={!canMilestone}
            activeMilestone={milestone}
            milestoneQueryRef={milestonesRef}
            onSelectionChange={setMilestoneCallback}
            {...sharedConfigProps}
          />
        )}
      </Box>
    </Suspense>
  )
}

try{ MetadataFooter.displayName ||= 'MetadataFooter' } catch {}